import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, {
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CustomLegend from './CustomLegend';
import CustomTooltip from './CustomTooltipTrends';
import {
  PERCENTAGE_TABLE,
  MetricBooleanMap,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import { DIRECT_REPORT_TYPE, TrendAndDetailsMetrics } from './types';
import { NoData } from '../../../../Util/NoData';
import Toggle from '@amzn/meridian/toggle';
import DateCompare from '../../../../Types/DateCompare';
import {
  calculateChartDataMinMax,
  ChartData,
  chartLineColors,
  getReportingTeamForDirectReports,
  hasChartDataValidDataPoints,
  sortChartDataByTimestamp,
  transformTrendData,
} from './helper';
import { TeamProfile } from '../../../../Types/TeamProfile';
import Select, { SelectOption } from '@amzn/meridian/select';
import Divider from '@amzn/meridian/divider';
import { StateContext } from '../../../../GlobalStateStore';
import CustomDot from './CustomDot';
import { TrendCategory } from '@amzn/aet-galactus-kit';
import { useLocation } from 'react-router';

interface CSAGraphViewProps {
  trendAndDetailsMetrics: TrendAndDetailsMetrics[];
  dateSelection: string;
  metricCategory: string;
  teamProfile: TeamProfile;
  clickstream?: any;
}
let runOnce = true;

const CSAGraphView: React.FC<CSAGraphViewProps> = ({
  trendAndDetailsMetrics,
  metricCategory,
  dateSelection,
  teamProfile,
  clickstream,
}: CSAGraphViewProps): React.ReactElement => {
  const intl = useIntl();

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState?.state;
  const { cscEntityPrefix, isOm } = inspireOptions.data;
  const [checked, setChecked] = useState(true);
  const [csaList, setCsaList] = useState<
    { userId: string; displayName: string }[]
  >([]);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const csaParam = params.get('selectedCSA');
  const [csaSelection, setCsaSelection] = useState(csaParam ?? '');
  const [chartTrends, setChartTrends] = useState<ChartData[]>();
  const [dataKeys, setDataKeys] = useState<string[]>([]);
  const [dataMetricIsPercentage, setDataMetricIsPercentage] =
    useState<boolean>(false);
  const [dataMin, setDataMin] = useState(0);
  const [dataMax, setDataMax] = useState(100);
  const [tempBox, setTempBox] = useState();
  const [chartTrendsContainsData, setChartTrendsContainsData] = useState(false);
  const tmpSelectState = useRef('');

  const trends = trendAndDetailsMetrics[0]?.metricDatas?.trend;

  const directReportType = isOm
    ? DIRECT_REPORT_TYPE.TM
    : DIRECT_REPORT_TYPE.ASSOCIATE;

  function changeCsaSelected(value: string) {
    tmpSelectState.current = value;
    setCsaSelection(value);
  }

  useEffect(() => {
    if (
      !trendAndDetailsMetrics ||
      !trendAndDetailsMetrics[0] ||
      csaSelection === ''
    ) {
      return;
    }

    if (!trendAndDetailsMetrics[0].metricDatas?.trend?.length) {
      setChartTrendsContainsData(false);
      return;
    }

    const selectedUserData = trendAndDetailsMetrics.find(
      item => item.userID === `${cscEntityPrefix}:${csaSelection}`
    );
    if (
      !selectedUserData ||
      !selectedUserData.metricDatas ||
      !selectedUserData.metricDatas.trend
    )
      return;
    const localChartTrends = transformTrendData(
      selectedUserData.metricDatas.trend
    );
    setChartTrends(sortChartDataByTimestamp(localChartTrends));
    if (!localChartTrends) return;
    setDataKeys(
      localChartTrends.length > 0
        ? Object.keys(localChartTrends[0]).filter(key => key !== 'timestamp')
        : []
    );
    setDataMetricIsPercentage(
      PERCENTAGE_TABLE[dataKeys[0] as keyof MetricBooleanMap]
    );
    const { dataMin: localDataMin, dataMax: localDataMax } =
      calculateChartDataMinMax(localChartTrends, dataMetricIsPercentage);
    setDataMin(localDataMin);
    setDataMax(localDataMax);
    setChartTrendsContainsData(hasChartDataValidDataPoints(localChartTrends));
  }, [trendAndDetailsMetrics, csaSelection]);

  useEffect(() => {
    if (teamProfile) {
      const sortedUserList = getReportingTeamForDirectReports(
        teamProfile.reportingTeam
      )
        .map(user => ({
          userId: user.relatedUser.userID,
          displayName: user.relatedUser.displayName,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
      setCsaList(sortedUserList);
    }
  }, [teamProfile]);

  setTimeout(() => {
    runOnce = false;
  }, 100);

  return (
    <Column width='100%' spacing='none' spacingInset='none none 500 none'>
      <>
        <>
          <Row spacingInset='300'>
            {metricCategory === TrendCategory.COMPLIANCE ? (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_compliance_metrics'
                  defaultMessage='{directReportType} Compliance Metrics'
                  values={{ directReportType }}
                />
              </Text>
            ) : metricCategory === TrendCategory.OTHER ? (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_other_metrics'
                  defaultMessage='{directReportType} Other Metrics'
                  values={{ directReportType }}
                />
              </Text>
            ) : metricCategory === TrendCategory.EFFICIENCY ? (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_efficiency_metrics'
                  defaultMessage='{directReportType} Efficiency Metrics'
                  values={{ directReportType }}
                />
              </Text>
            ) : metricCategory === TrendCategory.QUALITY ? (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_quality_metrics'
                  defaultMessage='{directReportType} Quality Metrics'
                  values={{ directReportType }}
                />
              </Text>
            ) : (
              <Text type='h200'>
                <FormattedMessage
                  id='cs_perfportal_metrics_nothing_to_show'
                  defaultMessage='Nothing to show.'
                />
              </Text>
            )}
          </Row>
          <Row width='100%' widths='fill' spacingInset='300'>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {dateSelection === DateCompare.DAY ? (
                  <Text type='b200'>
                    <FormattedMessage
                      id='cs_perfportal_metrics_tab_subtitle_days_view'
                      defaultMessage='Showing individual {directReportType} trends over the past 15 days.'
                      values={{ directReportType }}
                    />
                  </Text>
                ) : dateSelection === DateCompare.WEEK ? (
                  <Text type='b200'>
                    <FormattedMessage
                      id='cs_perfportal_metrics_tab_subtitle_weeks_view'
                      defaultMessage='Showing individual {directReportType} trends over the past 8 weeks.'
                      values={{ directReportType }}
                    />
                  </Text>
                ) : (
                  <Text type='b200'>
                    <FormattedMessage
                      id='cs_perfportal_metrics_tab_subtitle_months_view'
                      defaultMessage='Showing individual {directReportType} trends over the past 2 months.'
                      values={{ directReportType }}
                    />
                  </Text>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div>
                  {' '}
                  <Text type='b300'>
                    <FormattedMessage
                      id='cs_perfportal_network_benchmark'
                      defaultMessage='Network Benchmark'
                    />
                  </Text>
                </div>
                <div>
                  {' '}
                  <Toggle checked={checked} onChange={setChecked}>
                    {checked}
                  </Toggle>
                </div>
              </div>
            </div>
          </Row>
          <Divider spacingBefore='400' spacingAfter='400' />
          <Row width='100%' widths='fill' spacingInset='300'>
            <div style={{ width: '288px' }}>
              <Select
                label={intl.formatMessage({
                  id: 'cs_perfportal_direct_report_name',
                  defaultMessage: `${directReportType} Name`,
                })}
                value={csaSelection}
                width={288}
                key='CsaDate'
                data-cy='CsaDate'
                onChange={changeCsaSelected}
                size='xlarge'
              >
                {csaList.map(userItem => (
                  <SelectOption
                    label={userItem.displayName}
                    value={userItem.userId}
                    key={`${userItem.userId}`}
                    {...clickstream}
                    data-csa-c-content-id='metrics-associate-select'
                    data-csa-c-type='select'
                    data-csa-c-tag3={userItem.userId}
                  />
                ))}
              </Select>
            </div>
          </Row>
          {/* begin chart */}
          {!chartTrendsContainsData && <NoData />}
          {chartTrends && chartTrendsContainsData && (
            <Column
              width='100%'
              alignmentHorizontal='end'
              spacing='300'
              spacingInset='300'
            >
              <ResponsiveContainer width='99%' height={384}>
                <LineChart
                  data={chartTrends}
                  margin={{ top: 5, right: 30, left: -25, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis
                    dataKey='timestamp'
                    interval={0}
                    dx={0}
                    dy={20}
                    angle={0}
                    tick={{ fontSize: 10 }}
                    padding={
                      dateSelection === DateCompare.MONTH
                        ? { left: 50, right: 50 }
                        : { left: 20, right: 20 }
                    }
                    height={50}
                    minTickGap={-200}
                  >
                    {!chartTrendsContainsData && (
                      <Label
                        value={intl.formatMessage({
                          id: 'cs_perfportal_noDataToReport',
                          defaultMessage: 'No data to report',
                        })}
                        position='top'
                      />
                    )}
                  </XAxis>
                  <YAxis
                    type='number'
                    allowDecimals={false}
                    tick={{ fontSize: 10 }}
                    domain={[dataMin, dataMax]}
                    allowDataOverflow
                    padding={{ top: 20, bottom: 20 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {dataKeys.map((key, index) =>
                    checked || (!checked && !key.includes('#netavg')) ? (
                      <Line
                        name={key}
                        dataKey={key}
                        stroke={chartLineColors[index]}
                        strokeWidth={3}
                        type='monotone'
                        dot={
                          <CustomDot
                            chartTrends={chartTrends}
                            dataKey={key}
                            color={chartLineColors[index]}
                          />
                        }
                        strokeDasharray={
                          key.includes('#netavg') ? '5 2' : 'none'
                        }
                        isAnimationActive={false}
                      />
                    ) : null
                  )}
                  <Legend
                    verticalAlign='bottom'
                    align='left'
                    wrapperStyle={{ fontSize: '16px', marginLeft: '50px' }}
                    content={(props: any): ReactElement => (
                      <CustomLegend payload={props.payload} />
                    )}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Column>
          )}
        </>
      </>
    </Column>
  );
};

export default CSAGraphView;
